import { academyApi } from '@mathflat/domain/Academy'
import { curriculumApi } from '@mathflat/domain/Curriculum'
import type { Student } from '@mathflat/domain/Student'
import { studentApi } from '@mathflat/domain/Student'
import { userApi } from '@mathflat/domain/User'
import { bindI18nBrand } from '@mathflat/shared/i18n/i18n'

import { studentAppSettingLoader } from '~/@common/api/studentAppSettingLoader'
import { UserPermission } from '~/@common/models/(Permission)/UserPermission'
import { remoteStorageService } from '~/@common/services/remoteStorage.service'
import { commonRepo } from '~/@common/services/repo.service'
import { webviewService } from '~/@common/services/webview.service'
import { envUtils } from '~/@common/utils/envUtils'
import { buildReportContext } from '~/@common/utils/errorReporting/reportContext'
import { reporter } from '~/@common/utils/errorReporting/reporter'

let latestLoadId = 0

/** 학부모 자녀 전환처럼 연달아 불리면 응답 순서가 뒤집힐 수 있어, 마지막 호출만 상태에 반영한다. */
export const loadInitialData = async (
  studentId = localStorage.getItem('studentId'),
  restrictions?: string[],
) => {
  // studentId 없이 진입하는 경로는 호출부에서 모두 가드되므로 방어적으로 조기 종료한다.
  if (!studentId) return

  remoteStorageService.initRelationId(studentId)
  const loadId = ++latestLoadId
  const isStale = () => loadId !== latestLoadId

  try {
    const [academy, appSetting, student, curriculumCore, me] = await Promise.all([
      academyApi.getAcademy(),
      studentAppSettingLoader.getStudentAppSettings(studentId as Student['id']),
      studentApi.getStudent(studentId as Student['id']),
      curriculumApi.getCurriculumCore(),
      userApi.getMe(),
    ])
    if (isStale()) return

    commonRepo.setAcademy(academy)

    // 로그인 페이지로 첫 진입한 경우 부팅 시점엔 academy 가 없어 default 번들만 깔려 있다.
    // 로그인 완료 후 받아온 academy 의 brand 번들을 주입하고 remote 언어팩을 시도한다.
    // 후속 화면이 default 로 mount 되어 brand 문구가 늦게 반영되는 race 를 막기 위해 await 한다.
    await bindI18nBrand(
      { academyGroupType: academy.academyGroupType, customerType: academy.customerType },
      academy.languagePackFileUrl,
    )
    if (isStale()) return

    const _student = {
      ...student,
      appSetting,
    }

    commonRepo.setStudent(_student)
    commonRepo.setCurriculumCore(curriculumCore)
    const isSchoolflat =
      envUtils.isSchool || !!(academy.customerType && academy.customerType.includes('B2G'))

    // restrictions 출처 우선순위:
    //   1. login / onReissueSuccess 응답 (param 으로 전달됨, 가장 최신)
    //   2. me.restrictions (새로고침 시 login response를 담은 스토어 휘발됨)
    commonRepo.setPermission(
      new UserPermission({
        userRole: me.userType === 'PARENT' ? 'PARENT' : 'STUDENT',
        academyAuthority: academy.authority,
        subscribeItems: academy.subscribeItems,
        isSchoolflat,
        restrictions: restrictions ?? me.restrictions ?? [],
      }),
    )

    // SELF_LEARNING restriction 차단 시엔 보충학습 관련 remote-storage 도 조회 X
    if (commonRepo.permission.byFeature.isAllowed('SELF_LEARNING')) {
      remoteStorageService.get('isVisitedSelfLearning')
      remoteStorageService.get('isCompletedSelfLearning')
    }

    const reportContext = buildReportContext({
      studentId: _student.id,
      academyId: academy.academyId,
      userType: me.userType,
      isSchoolflat,
      isApp: webviewService.isApp,
    })
    reporter.setUser(reportContext.user)
    reporter.setTags(reportContext.tags)
    commonRepo.clearLoadFailure()
  } catch (e) {
    console.error(e)
    if (isStale()) return
    commonRepo.setLoadFailure(e instanceof Error ? e.message : 'unknown error')
  }
}
